import React from "react";
import { Link } from "react-router-dom";

/* ═══════════════════════════════════════════════════════════════════
   ChainFlow — Global Brand Component (single source of truth)
   ───────────────────────────────────────────────────────────────────
   The ONE component that renders the official ChainFlow logo
   everywhere across the product, the marketing site, transactional
   emails, loading states and PDFs.

   Source-of-truth raster assets (transparent PNG, 4× DPI):
     /chainflow-logo.png         — full lockup (light surfaces)
     /chainflow-logo-light.png   — full lockup (dark surfaces)
     /chainflow-icon.png         — icon-only square
     /chainflow-wordmark.png     — wordmark only (light surfaces)
     /chainflow-wordmark-light.png — wordmark only (dark surfaces)

   Why <img> rather than inline SVG?
     • Pixel-identical to the uploaded master logo at every render
     • Browser-rendered raster scaling stays crisp at every size
       used in the product (16–256 px), and looks vector-sharp on
       hi-DPI displays because the source PNGs are 4× resolution
     • Zero risk of glyph drift across the codebase — there is no
       hand-coded path data to fall out of sync with the brand asset

   PROPS
     variant   : "full" (default) | "icon" | "wordmark"
     size      : number — height in px (default 32). Width auto-scales.
     tone      : "auto" (default · navy "Chain" on light surfaces)
                 "light"  · white "Chain" — for dark surfaces
     to        : optional react-router path — wraps logo in <Link>
     href      : optional anchor href — wraps in <a>
     ariaLabel : accessibility label (default "ChainFlow")
     className : extra Tailwind classes on the outer element
     style     : extra inline styles
     onClick   : optional click handler (when not using to/href)
     testid    : data-testid (default "chainflow-logo")

   Locked aspect ratios (so spacing is identical everywhere):
     full     1611 × 378  → 4.262 : 1
     icon      394 × 394  → 1 : 1
     wordmark 1300 × 236  → 5.508 : 1
   ─────────────────────────────────────────────────────────────────── */

const ASSETS = {
  full: {
    auto:  "/chainflow-logo.png",
    light: "/chainflow-logo-light.png",
  },
  icon: {
    auto:  "/chainflow-icon.png",
    light: "/chainflow-icon.png", // icon glyph is gradient — readable on both
  },
  wordmark: {
    auto:  "/chainflow-wordmark.png",
    light: "/chainflow-wordmark-light.png",
  },
};

const RATIO = {
  full:     1611 / 378,
  icon:     1,
  wordmark: 1300 / 236,
};

export const ChainFlowLogo = ({
  variant   = "full",
  size      = 32,
  tone      = "auto",
  to,
  href,
  ariaLabel = "ChainFlow",
  className = "",
  style,
  onClick,
  testid    = "chainflow-logo",
}) => {
  const toneKey = tone === "light" ? "light" : "auto";
  const src = ASSETS[variant]?.[toneKey] || ASSETS.full.auto;
  const ratio = RATIO[variant] ?? RATIO.full;
  const height = size;
  const width  = Math.round(size * ratio);

  const img = (
    <img
      src={src}
      alt={ariaLabel}
      width={width}
      height={height}
      draggable={false}
      style={{
        height,
        width,
        display: "block",
        // Pre-emptive smooth scaling on hi-DPI screens
        imageRendering: "auto",
        userSelect: "none",
        ...style,
      }}
      className={className}
      onClick={onClick}
      data-testid={testid}
    />
  );

  if (to) {
    return (
      <Link
        to={to}
        className="inline-flex items-center shrink-0"
        aria-label={ariaLabel}
        data-testid={`${testid}-link`}
      >
        {img}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        className="inline-flex items-center shrink-0"
        aria-label={ariaLabel}
        data-testid={`${testid}-link`}
      >
        {img}
      </a>
    );
  }
  return (
    <span
      className="inline-flex items-center shrink-0"
      aria-label={ariaLabel}
      role="img"
    >
      {img}
    </span>
  );
};

export default ChainFlowLogo;

/* Backwards-compat exports — older imports continue to work.
   They all resolve to the same canonical ChainFlowLogo component.
   Legacy props (iconSize, wordSize) are mapped to the new sizing system. */
export const BrandLogo = ({ iconSize, wordSize, ...rest }) => (
  <ChainFlowLogo size={iconSize || wordSize || rest.size || 32} {...rest} />
);
export const BrandIcon = ({ size = 32, className = "", tone = "auto" }) =>
  <ChainFlowLogo variant="icon" size={size} tone={tone} className={className} />;
export const BrandWord = ({ size = 22, className = "", tone = "auto" }) =>
  <ChainFlowLogo variant="wordmark" size={size} tone={tone} className={className} />;
